// src/pages/Filiacao.tsx
import React, { useState } from 'react';
import { FileText, ShieldCheck, BadgeCheck, ChevronDown } from 'lucide-react';
import FormularioFiliacaoPublico from '../components/Filiacao/FormularioFiliacaoPublico';

const BRAND = {
  gold: '#D4AF37',
  goldDeep: '#B88917',
  charcoal: '#1F2328',
};

// ── Accordion genérico ────────────────────────────────────────
const Accordion: React.FC<{
  items: { title: string; content: React.ReactNode }[];
  defaultOpen?: number;
}> = ({ items, defaultOpen = 0 }) => {
  const [open, setOpen] = useState<number | null>(defaultOpen);
  return (
    <div className="divide-y" style={{ borderColor: `${BRAND.gold}22` }}>
      {items.map((item, i) => (
        <div key={i}>
          <button
            onClick={() => setOpen(open === i ? null : i)}
            className="w-full flex items-center justify-between gap-4 py-4 text-left transition-colors hover:opacity-80"
          >
            <span className="font-bold text-sm" style={{ color: BRAND.charcoal }}>
              {item.title}
            </span>
            <ChevronDown
              className="w-4 h-4 flex-shrink-0 transition-transform duration-200"
              style={{
                color: BRAND.goldDeep,
                transform: open === i ? 'rotate(180deg)' : 'rotate(0deg)',
              }}
            />
          </button>
          {open === i && (
            <div className="pb-4 text-sm text-gray-600 leading-relaxed">
              {item.content}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

// ── Tab strip genérico ────────────────────────────────────────
const Tabs: React.FC<{
  tabs: { label: string; content: React.ReactNode }[];
}> = ({ tabs }) => {
  const [active, setActive] = useState(0);
  return (
    <div>
      {/* Tab buttons */}
      <div className="flex flex-wrap gap-2 mb-5">
        {tabs.map((tab, i) => (
          <button
            key={i}
            onClick={() => setActive(i)}
            className="px-4 py-2 rounded-full text-sm font-semibold transition-all border"
            style={
              active === i
                ? {
                    background: BRAND.charcoal,
                    color: '#fff',
                    borderColor: BRAND.charcoal,
                  }
                : {
                    background: 'white',
                    color: BRAND.charcoal,
                    borderColor: `${BRAND.gold}44`,
                  }
            }
          >
            {tab.label}
          </button>
        ))}
      </div>
      {/* Tab content */}
      <div>{tabs[active].content}</div>
    </div>
  );
};

const Filiacao: React.FC = () => {
  // ── Dados dos benefícios principais ──────────────────────────
  const beneficiosPrincipais = [
    {
      title: 'Carteira de Habilitação Profissional',
      content:
        'Receberá sua identidade profissional em cartão PVC com selo de rastreabilidade QR Code para consulta pública da sua função no site da Federação.',
    },
    {
      title: 'Licença Educacional e Institucional',
      content:
        'Contará com a documentação de Profissional Educador para lecionar e qualificar novos profissionais e educadores, além da licença do estabelecimento para fins de fiscalização e processos internacionais.',
    },
    {
      title: 'Reconhecimento nacional e internacional',
      content:
        'Com o diploma emitido pela FEPCSSP, você estará habilitado para atuar no Brasil e com possibilidade de atuação em mais de 100 países, com respaldo para obtenção de licença de trabalho (cada país com normas distintas).',
    },
    {
      title: 'Divulgação',
      content:
        'Você e seu espaço recebem um link de divulgação em nosso site para pesquisa de Clientes, Alunos, Empresas e até mesmo para base de fiscalização, te dando mais credibilidade e o respaldo necessário.',
    },
  ];

  // ── Tabs: benefícios exclusivos + descontos ───────────────────
  const exclusivoTabs = [
    {
      label: 'Benefícios',
      content: (
        <Accordion
          items={[
            {
              title: 'Rede de excelência',
              content:
                'Junte-se a profissionais renomados que construíram suas histórias conosco. Assegure a legalidade e credibilidade do seu estabelecimento.',
            },
            {
              title: 'Bônus: acesso a cursos',
              content:
                'Acesso a cursos on-line e presenciais como bônus exclusivo que a Federação realizar.',
            },
            {
              title: 'Formação e diplomação de alunos',
              content:
                'Você estará apto a formar e realizar a diplomação do seu aluno formando: emissão do diploma de habilitação como profissional.',
            },
            {
              title: 'Base didática complementar',
              content:
                'Receba em seu e-mail toda base didática complementar, contratos pré-elaborados e outros materiais. Tenha acesso a diversas apostilas para seu negócio: aluno, colaborador ou professor.',
            },
          ]}
          defaultOpen={0}
        />
      ),
    },
    {
      label: 'Descontos',
      content: (
        <ul className="space-y-3 text-sm text-gray-700 leading-relaxed pt-1">
          {[
            'Apoio jurídico e institucional para o seu negócio com desconto.',
            'Desconto exclusivo para criação de site institucional com a ORUS.',
            'Desconto exclusivo na patente e registro de sua marca.',
            'Desconto exclusivo em marcas parceiras da Federação.',
            'Desconto no Plano de Benefícios com desconto em Cinemas, Farmácias, Lojas e até mesmo assistência Médica Online com Teleconsulta em minutos.',
            'Acesso a Feiras e Eventos Gratuitos que a Federação promova ou tenha participação e parceria como: Barber Week, Beauty Fair, Profissional Fair, Beauty Glow e muito mais.',
            'Acesso ao Brasão para divulgação e elevar seu reconhecimento.',
          ].map((item, i) => (
            <li key={i} className="flex gap-2">
              <span className="font-black mt-0.5 flex-shrink-0" style={{ color: BRAND.goldDeep }}>•</span>
              {item}
            </li>
          ))}
        </ul>
      ),
    },
  ];

  return (
    <div className="bg-white">
      {/* HERO COMPACTO */}
      <section className="relative overflow-hidden border-b" style={{ borderColor: `${BRAND.gold}22` }}>
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-[#FFF6D6] via-white to-white" />
        <div
          className="absolute -top-24 -right-24 h-72 w-72 rounded-full blur-3xl opacity-40"
          style={{ background: `radial-gradient(circle, ${BRAND.gold} 0%, transparent 70%)` }}
        />
        <div
          className="absolute -bottom-24 -left-24 h-72 w-72 rounded-full blur-3xl opacity-25"
          style={{ background: `radial-gradient(circle, ${BRAND.charcoal} 0%, transparent 70%)` }}
        />

        <div className="relative container mx-auto px-6 md:px-12 py-8 md:py-10">
          <div className="mx-auto max-w-6xl grid md:grid-cols-[1fr,180px] lg:grid-cols-[1fr,220px] items-center gap-7 md:gap-10">
            <div className="text-center md:text-left">
              <div className="inline-flex items-center gap-2 rounded-full border px-4 py-2 bg-white/80 backdrop-blur mb-4" style={{ borderColor: `${BRAND.gold}55` }}>
                <span className="text-sm font-semibold" style={{ color: BRAND.charcoal }}>Filiação • Federação</span>
              </div>
              <h1 className="text-3xl md:text-5xl font-black tracking-tight" style={{ color: BRAND.charcoal }}>
                Filie-se à Federação
              </h1>
              <p className="text-base md:text-lg max-w-3xl leading-relaxed text-gray-700 mt-4">
                Preencha seu cadastro, envie os documentos para análise e acompanhe as próximas etapas da sua filiação.
              </p>
              <div className="flex flex-wrap justify-center md:justify-start gap-2 mt-5">
                {['Cadastro guiado', 'Envio seguro', 'Protocolo ao concluir'].map(item => (
                  <span key={item} className="inline-flex items-center gap-1.5 rounded-full bg-white border px-3 py-1.5 text-xs font-bold text-gray-700" style={{ borderColor: `${BRAND.gold}33` }}>
                    <BadgeCheck className="w-4 h-4" style={{ color: BRAND.goldDeep }} /> {item}
                  </span>
                ))}
              </div>
              <a href="#cadastro-filiacao" className="inline-flex items-center justify-center mt-6 px-6 py-3 rounded-xl font-black shadow-lg hover:-translate-y-0.5 transition" style={{ background: `linear-gradient(90deg, ${BRAND.goldDeep}, ${BRAND.gold})`, color: BRAND.charcoal }}>
                Iniciar cadastro
              </a>
            </div>
            <div className="flex justify-center md:justify-end">
              <img src="/imagem/Bras.png" alt="Logomarca Federação" className="h-28 md:h-40 lg:h-48 w-auto select-none drop-shadow-lg" />
            </div>
          </div>
        </div>
      </section>

      {/* CADASTRO — ação principal da página */}
      <section id="cadastro-filiacao" className="scroll-mt-24 bg-gray-50/70 py-10 md:py-14">
        <div className="container mx-auto px-4 md:px-12">
          <div className="mx-auto max-w-6xl">
            <div className="mb-7 text-center lg:text-left">
              <p className="text-xs font-black uppercase tracking-[0.2em]" style={{ color: BRAND.goldDeep }}>Comece agora</p>
              <h2 className="text-2xl md:text-3xl font-black mt-2" style={{ color: BRAND.charcoal }}>Cadastro de Filiação</h2>
              <p className="text-sm md:text-base text-gray-600 mt-2">CPF é o documento principal; CNPJ é opcional. Seu progresso fica salvo neste dispositivo.</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px] gap-6 items-start">
            <div
              className="rounded-3xl border bg-white shadow-lg overflow-hidden"
              style={{ borderColor: `${BRAND.gold}33` }}
            >
              <div className="p-5 md:p-8 bg-[linear-gradient(180deg,rgba(212,175,55,0.08)_0%,rgba(255,255,255,0.0)_55%)]">
                <FormularioFiliacaoPublico />
              </div>
            </div>

            <aside
              className="rounded-3xl border bg-white shadow-sm p-6 lg:sticky lg:top-24"
              style={{ borderColor: `${BRAND.gold}33` }}
            >
              <h3 className="text-lg font-black mb-4" style={{ color: BRAND.charcoal }}>
                Como funciona a análise?
              </h3>

              <Accordion
                items={[
                  {
                    title: '1) Envio do cadastro e documentos',
                    content: 'Você conclui o formulário e recebe um protocolo.',
                  },
                  {
                    title: '2) Comissão de avaliação',
                    content:
                      'A análise considera critérios técnicos e documentais conforme o processo interno.',
                  },
                  {
                    title: '3) Retorno oficial',
                    content: 'Você recebe aprovação ou solicitação de ajuste por e-mail.',
                  },
                  {
                    title: '4) Pagamento da taxa de filiação',
                    content:
                      'Após aprovação, realize o pagamento da taxa de investimento para se tornar um Filiado chancelado.',
                  },
                ]}
                defaultOpen={0}
              />

              <div className="mt-4 rounded-2xl p-4" style={{ backgroundColor: `${BRAND.charcoal}0D` }}>
                <p className="text-sm font-bold" style={{ color: BRAND.charcoal }}>
                  Dica rápida
                </p>
                <p className="text-sm text-gray-700 mt-1 leading-relaxed">
                  Tenha seus documentos em PDF ou imagem (JPG/PNG) para agilizar o envio.
                </p>
              </div>
            </aside>
            </div>
          </div>
        </div>
      </section>

      {/* CONTEÚDO INSTITUCIONAL PRESERVADO, APÓS O CADASTRO */}
      <section className="container mx-auto px-6 md:px-12 py-14 md:py-20">
        <div className="mx-auto max-w-6xl space-y-8">
          <div className="text-center max-w-3xl mx-auto">
            <p className="text-xs font-black uppercase tracking-[0.2em]" style={{ color: BRAND.goldDeep }}>Conheça as vantagens</p>
            <h2 className="text-2xl md:text-4xl font-black mt-2" style={{ color: BRAND.charcoal }}>O que a filiação oferece</h2>
            <p className="text-gray-600 mt-3 leading-relaxed">Todos os benefícios e informações continuam disponíveis para você consultar antes ou depois de iniciar seu cadastro.</p>
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            {[
              { title: 'Cadastro guiado', desc: 'Etapas claras e validação antes de enviar.', Icon: FileText },
              { title: 'Segurança', desc: 'Dados e documentos tratados com padrão profissional.', Icon: ShieldCheck },
              { title: 'Confirmação rápida', desc: 'Protocolo gerado ao concluir o envio.', Icon: BadgeCheck },
            ].map(({ title, desc, Icon }) => (
              <div key={title} className="rounded-2xl border bg-white p-5 shadow-sm" style={{ borderColor: `${BRAND.gold}33` }}>
                <div className="flex items-start gap-3">
                  <div className="h-10 w-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${BRAND.gold}1A` }}>
                    <Icon className="h-5 w-5" style={{ color: BRAND.goldDeep }} />
                  </div>
                  <div><p className="font-bold" style={{ color: BRAND.charcoal }}>{title}</p><p className="text-sm text-gray-600 leading-relaxed">{desc}</p></div>
                </div>
              </div>
            ))}
          </div>

          <div className="grid lg:grid-cols-2 gap-6 items-start">
            <div className="rounded-3xl border bg-white p-6 md:p-8 shadow-sm" style={{ borderColor: `${BRAND.gold}33` }}>
              <h3 className="text-xl md:text-2xl font-black mb-5" style={{ color: BRAND.charcoal }}>Ao se tornar um Filiado à Federação, você terá:</h3>
              <Accordion items={beneficiosPrincipais} defaultOpen={0} />
            </div>

            <div className="rounded-3xl border bg-white p-6 md:p-8 shadow-sm" style={{ borderColor: `${BRAND.gold}33` }}>
              <h3 className="text-xl md:text-2xl font-black" style={{ color: BRAND.charcoal }}>Benefícios e acesso exclusivo</h3>
              <p className="text-gray-700 mt-2 leading-relaxed mb-6">Junte-se a uma rede forte e tenha acesso a materiais, bônus e vantagens que impulsionam seu negócio.</p>
              <Tabs tabs={exclusivoTabs} />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="rounded-2xl border px-6 py-5" style={{ borderColor: `${BRAND.gold}33`, backgroundColor: `${BRAND.gold}0D` }}>
              <p className="font-black" style={{ color: BRAND.charcoal }}>Reconhecimento internacional</p>
              <p className="text-sm md:text-base text-gray-700 leading-relaxed mt-2">Com credenciais e documentação adequadas, você amplia seu alcance e fortalece sua credibilidade profissional.</p>
            </div>
            <div className="rounded-2xl border px-6 py-5" style={{ borderColor: `${BRAND.gold}33`, backgroundColor: `${BRAND.charcoal}0D` }}>
              <p className="font-black" style={{ color: BRAND.charcoal }}>Quem pode se filiar?</p>
              <p className="text-sm md:text-base text-gray-700 leading-relaxed mt-2">Profissionais, educadores, empresas do setor, escolas e academias da beleza podem solicitar filiação, conforme critérios de análise.</p>
            </div>
          </div>

          <div className="text-center pt-3">
            <a href="#cadastro-filiacao" className="inline-flex px-6 py-3 rounded-xl font-black border hover:bg-amber-50 transition" style={{ borderColor: BRAND.goldDeep, color: BRAND.charcoal }}>Voltar ao cadastro</a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Filiacao;
